export const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

export const idKey = (obj) => String(obj.id);

export const norm = (v = '') =>
  String(v).normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();

export const includes = (hay, needle) => norm(hay).includes(norm(needle));

export const getNome = (ent) => ent?.traducoes?.[0]?.nome || ent?.nome || '';

export const getDescricao = (ent) => ent?.traducoes?.[0]?.descricao || ent?.descricao || '';

export const toNum = (v) => {
  if (v == null) return 0;
  if (typeof v === 'string') {
    const parsed = Number(v.replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : 0;
  }
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export const formatBRLDisplay = (value) => {
  const num = Number(value) || 0;
  return `R$ ${num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const parseBRLInput = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return { display: '', numeric: 0 };
  const num = Number(digits) / 100;
  return {
    display: `R$ ${num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    numeric: num,
  };
};

export const calcTotalPedido = (p) => {
  if (!p) return 0;
  const itens = p.itens || [];
  return itens.reduce((acc, it) => {
    const compTotal = (it.complementos || []).reduce(
      (a, c) => a + (toNum(c.preco) || toNum(c.complemento?.preco)),
      0
    );
    const base = (toNum(it.preco_unit) || toNum(it.item?.preco)) + compTotal;
    return acc + base * (toNum(it.quantidade) || 1);
  }, 0);
};

export const getPedidoComandaLabel = (pedido) => {
  const raw =
    pedido?.comanda_id ??
    pedido?.comanda ??
    pedido?.comandaId ??
    pedido?.comanda_codigo ??
    pedido?.comandaCodigo;
  if (raw && typeof raw === 'object') return raw.codigo || raw.nome || raw.label || raw.id || 'Comanda';
  return raw ?? 'Sem comanda';
};

export const getPedidoMesaLabel = (pedido) => pedido?.mesa || 'Sem mesa';

export const getCompId = (comp) => comp?.id ?? comp?.complemento_id ?? comp?.complemento?.id;

export const menuPalette = {
  blue: '#1f4fbf',
  orange: '#f26b3a',
  ink: '#1c274c',
  paper: '#fffaf2',
  border: 'rgba(31,79,191,0.18)',
  muted: 'rgba(31,79,191,0.06)',
  categoriaPanelBg: 'linear-gradient(180deg, rgba(255,247,240,0.98), rgba(255,251,246,0.96))',
  categoriaSummaryBg: 'linear-gradient(90deg, rgba(242,107,58,0.10), rgba(255,255,255,0.92))',
  categoriaDetailsBg: 'rgba(255,249,244,0.82)',
  categoriaBorder: 'rgba(242,107,58,0.42)',
  pedidosPanelBg: 'linear-gradient(135deg, #fdf1e8 0%, #eef2ff 100%)',
  kanbanGroupBg: 'linear-gradient(135deg, rgba(31,79,191,0.07), rgba(242,107,58,0.05))',
  pedidoTileBg: '#fffaf5',
};

export const orderedStatuses = ['recebido', 'em_preparo', 'pronto', 'entregue'];

export const COMANDA_CLOSED_STATUSES = new Set(['finalizado', 'finalizada', 'cancelado', 'cancelada']);

export const isComandaFechada = (status) => COMANDA_CLOSED_STATUSES.has(String(status || '').toLowerCase());

export const CARDAPIO_VIRTUAL_STORAGE_KEY = 'onemenu:usar_cardapio_virtual';

export const CHANNEL_IN_PERSON = 'in_person';
export const CHANNEL_DELIVERY = 'delivery';

export const MENU_CHANNELS = Object.freeze([
  { code: CHANNEL_DELIVERY, label: 'Delivery', previewLabel: 'Ver delivery' },
  { code: CHANNEL_IN_PERSON, label: 'Salão', previewLabel: 'Ver salão' },
]);

export const DEFAULT_CHANNELS = Object.freeze({
  in_person: { ativo: true },
  delivery: { ativo: false },
});

export const CHANNEL_CODES = new Set(MENU_CHANNELS.map((channel) => channel.code));

export const normalizeMenuChannel = (value) => (
  CHANNEL_CODES.has(String(value || '').trim().toLowerCase())
    ? String(value).trim().toLowerCase()
    : CHANNEL_DELIVERY
);

export const getChannelMeta = (code) => MENU_CHANNELS.find((channel) => channel.code === code) || MENU_CHANNELS[0];

export const normalizeCanais = (canais, fallbackAtivo = true) => {
  const base = {
    [CHANNEL_IN_PERSON]: { ...DEFAULT_CHANNELS[CHANNEL_IN_PERSON], ativo: fallbackAtivo !== false },
    [CHANNEL_DELIVERY]: { ...DEFAULT_CHANNELS[CHANNEL_DELIVERY] },
  };
  if (!canais || typeof canais !== 'object') return base;
  const source = Array.isArray(canais)
    ? canais.reduce((acc, entry) => {
        const code = String(entry?.canal_codigo || entry?.canal || entry?.codigo || '').trim().toLowerCase();
        if (CHANNEL_CODES.has(code)) acc[code] = entry;
        return acc;
      }, {})
    : canais;
  MENU_CHANNELS.forEach(({ code }) => {
    const cfg = source[code];
    if (cfg && typeof cfg === 'object') {
      base[code] = {
        ...base[code],
        ...cfg,
        ativo: cfg.ativo !== false,
      };
    } else if (typeof cfg === 'boolean') {
      base[code] = {
        ...base[code],
        ativo: cfg,
      };
    }
  });
  return base;
};

export const getChannelConfig = (entity = {}, channelCode = CHANNEL_DELIVERY) => {
  const channel = normalizeMenuChannel(channelCode);
  const fallbackAtivo = entity.ativo_base ?? (entity.ativo !== false);
  const canais = normalizeCanais(entity.canais, fallbackAtivo);
  return canais[channel] || DEFAULT_CHANNELS[channel] || DEFAULT_CHANNELS[CHANNEL_DELIVERY];
};

export const buildChannelsWithActive = (entity = {}, channelCode = CHANNEL_DELIVERY, active = true) => {
  const channel = normalizeMenuChannel(channelCode);
  const fallbackAtivo = entity.ativo_base ?? (entity.ativo !== false);
  const canais = normalizeCanais(entity.canais, fallbackAtivo);
  return {
    ...canais,
    [channel]: {
      ...(canais[channel] || {}),
      ativo: active !== false,
    },
  };
};

export const buildChannelsWithConfig = (entity = {}, channelCode = CHANNEL_DELIVERY, config = {}) => {
  const channel = normalizeMenuChannel(channelCode);
  const fallbackAtivo = entity.ativo_base ?? (entity.ativo !== false);
  const canais = normalizeCanais(entity.canais, fallbackAtivo);
  return {
    ...canais,
    [channel]: {
      ...(canais[channel] || {}),
      ...config,
    },
  };
};

export const getChannelPriceOverrideInput = (cfg = {}) => (
  cfg.preco_override_input ?? (cfg.preco_override != null ? formatBRLDisplay(cfg.preco_override) : '')
);

export const applyCardapioChannelView = (cardapio = {}, channelCode = CHANNEL_DELIVERY) => {
  const channel = normalizeMenuChannel(channelCode);
  const cfg = getChannelConfig(cardapio, channel);
  const baseAtivo = cardapio.ativo !== false;
  const channelAtivo = cfg.ativo !== false;
  return {
    ...cardapio,
    ativo_base: baseAtivo,
    canal: channel,
    canal_ativo: channelAtivo,
    ativo: baseAtivo && channelAtivo,
  };
};

export const applyItemChannelView = (item = {}, channelCode = CHANNEL_DELIVERY) => {
  const channel = normalizeMenuChannel(channelCode);
  const cfg = getChannelConfig(item, channel);
  const baseAtivo = item.ativo !== false;
  const channelAtivo = cfg.ativo !== false;
  const precoBase = item.preco ?? 0;
  const promoBase = item.preco_promocional ?? null;
  return {
    ...item,
    ativo_base: baseAtivo,
    canal: channel,
    canal_ativo: channelAtivo,
    ativo: baseAtivo && channelAtivo,
    preco_base: precoBase,
    preco_promocional_base: promoBase,
    preco: cfg.preco_override != null ? Number(cfg.preco_override) : precoBase,
    preco_promocional: cfg.preco_promocional_override != null ? Number(cfg.preco_promocional_override) : promoBase,
  };
};
